import {
  LoanPipelineColumnDisplayMode,
  LoanPipelineColumnValueType,
  LoanPipelineSpecificColumnId,
  PipelineColumnMetadataKey,
  relativeDaysExcludedColumns,
} from 'constants/loanPipelines';
import { jsonParse } from 'utils/json';
import { getProgressPercentage } from 'utils/loanApplication';

export const normalizeLoanPipelineViewColumnData = data =>
  data.reduce((acc, { value, columnId, valueEntityId }) => {
    const primaryColumnId = valueEntityId || columnId;

    try {
      switch (columnId) {
        case LoanPipelineSpecificColumnId.LOAN_STATUS: {
          const data = jsonParse(value || null) || {};

          acc[primaryColumnId] = { ...data, progressPercentage: getProgressPercentage(data) };
          break;
        }

        case LoanPipelineSpecificColumnId.MILESTONE_STATUS: {
          const data = jsonParse(value || null) || {};

          acc[primaryColumnId] = {
            ...data,
            name: data.Name,
            progressPercentage: Math.floor(data.Progress),
          };
          break;
        }

        case LoanPipelineSpecificColumnId.PROGRESS_PERCENTAGE: {
          acc[primaryColumnId] = {
            progressPercentage: Math.floor(value),
          };
          break;
        }

        case LoanPipelineSpecificColumnId.CONDITION_TASK_IDS: {
          const data = jsonParse(value || null) || [];

          acc[primaryColumnId] = data;
          break;
        }

        default:
          acc[primaryColumnId] = value;
          break;
      }
    } catch {
      acc[primaryColumnId] = value;
    }

    return acc;
  }, {});

// DisplayMode rides the per-column `metadata` bag; an unknown stored value reads as the default
// rendering so a hand-edited bag can never break the table.
export const getColumnDisplayMode = metadata => {
  const storedValue = metadata?.[PipelineColumnMetadataKey.DISPLAY_MODE];

  return Object.values(LoanPipelineColumnDisplayMode).includes(storedValue) ? storedValue : null;
};

// Writes stay non-destructive: sibling keys (e.g. the lock-status threshold) survive a mode flip,
// and clearing the mode removes only its own key.
export const setColumnDisplayMode = (metadata, displayMode) => {
  const { [PipelineColumnMetadataKey.DISPLAY_MODE]: _omitted, ...rest } = metadata ?? {};
  const nextMetadata = displayMode
    ? { ...rest, [PipelineColumnMetadataKey.DISPLAY_MODE]: displayMode }
    : rest;

  return Object.keys(nextMetadata).length > 0 ? nextMetadata : null;
};

const RELATIVE_DAYS_SORT_KEY_SUFFIX = '::relativeDays';

export const getColumnSortKey = ({ primaryDefinitionId, displayMode }) =>
  displayMode === LoanPipelineColumnDisplayMode.RELATIVE_DAYS
    ? `${primaryDefinitionId}${RELATIVE_DAYS_SORT_KEY_SUFFIX}`
    : primaryDefinitionId;

// A relative-days instance sorts through its twin's ColumnId with the direction inverted
// (fewer days since = later date), so the sort key carries the instance identity.
export const parseColumnSortKey = sortKey => {
  const isRelativeDays = !!sortKey && sortKey.endsWith(RELATIVE_DAYS_SORT_KEY_SUFFIX);

  return {
    definitionId: isRelativeDays
      ? sortKey.slice(0, -RELATIVE_DAYS_SORT_KEY_SUFFIX.length)
      : sortKey,
    isRelativeDays,
  };
};

export const invertSortDirection = direction => (direction === 'asc' ? 'desc' : 'asc');

// Shared generic definition — see selectPipelinesColumnDefinitions.
export const getLoanPipelineColumnDefinition = ({
  definitionsByPrimaryId,
  primaryDefinitionId,
  columnId,
}) => definitionsByPrimaryId?.[primaryDefinitionId] ?? definitionsByPrimaryId?.[columnId];

export const getColumnInstanceKey = ({ primaryDefinitionId, id, displayMode }) =>
  `${primaryDefinitionId}-${id}${displayMode ? `-${displayMode}` : ''}`;

// Excluded ids are DateTime columns whose cells render something else entirely (Lock Status
// shows a traffic-light status), so they never render as a day count — the config hides the
// mode for them, and this keeps a hand-edited bag from overriding their rendering.
export const checkIsRelativeDaysColumn = (valueType, displayMode, id) =>
  valueType === LoanPipelineColumnValueType.DATE_TIME &&
  displayMode === LoanPipelineColumnDisplayMode.RELATIVE_DAYS &&
  !relativeDaysExcludedColumns.includes(id);
